import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import HelpModal from 'components/NewDesign/HelpModal';
import { ReactComponent as InfoCircleIcon } from 'assets/images/icon_info_circle.svg';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useGroupLabels from 'hooks/useGroupLabels';
import { clubFeaturesOf, clubForId, organisationWordOf } from 'utils/clubFeatures';

// Help button + explainer dialog for the Age Results pages (track and high
// jump). Who is ranked differs by meet type: single-club meets rank full
// members only; multi-club meets rank every registered athlete.
const AgeResultsHelp = ({ isMultiClub }) => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  // "Age Results", or "Year Results" for a club that groups by year level.
  const { words } = useGroupLabels();
  const resultsName = `${words.value} Results`;
  // A club that has visitors (an athletics club) is told that guests and
  // visitors are not ranked. A school has no visitors, only walk-up guests.
  const { clubId } = useParams();
  const hasVisitors = useSelector(
    (state) => clubFeaturesOf(clubForId(state.user, clubId)).visitors
  );
  // "club members", "club points" and "club records", or "school" for a school (Doug, 2026-10-27).
  const organisationWord = useSelector((state) =>
    organisationWordOf(clubForId(state.user, clubId))
  );

  return (
    <>
      <CustomNewButton
        variant="outline"
        icon={<InfoCircleIcon />}
        onClick={() => setShowHelpModal(true)}
        customStyle={{ backgroundColor: '#fefce8' }}
      >
        Who&apos;s in {resultsName}?
      </CustomNewButton>

      <HelpModal
        open={showHelpModal}
        title={`Who's in ${resultsName}?`}
        onClose={() => setShowHelpModal(false)}
      >
        <h3>One Combined List Per Round Type</h3>
        <p>
          {resultsName} combine all rounds of the same type into a single ranked
          list. If an event ran three heats and two semis, every heat result is
          ranked together in one Heats list and every semi result in a separate
          Semis list — no matter which heat or semi an athlete ran in.
        </p>

        <h3>Who Is Ranked</h3>
        {isMultiClub ? (
          <p>
            Every athlete registered for this meet is ranked — members, guests
            and visitors alike.
          </p>
        ) : (
          <>
            <p>
              Only {organisationWord} members are ranked. These placings decide{' '}
              {organisationWord} points and count toward season bests and{' '}
              {organisationWord} records.
            </p>
            {hasVisitors ? (
              <p>
                Guest and Visitor athletes are not ranked here because they do
                not earn {organisationWord} points or season bests. Their results still
                appear in Event View - Round Results with a Guest or Visitor
                badge.
              </p>
            ) : (
              <p>
                Guest athletes are not ranked here because they do not earn{' '}
                {organisationWord} points or season bests. Their results still appear in
                Event View - Round Results with a Guest badge.
              </p>
            )}
          </>
        )}
      </HelpModal>
    </>
  );
};

AgeResultsHelp.propTypes = {
  isMultiClub: PropTypes.bool,
};

export default AgeResultsHelp;
